import { onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue'

/** Сырой жест (px) для срабатывания перезагрузки при отпускании. */
const PULL_RELOAD_RAW_THRESHOLD_PX = 100

/** Максимум визуальной «резинки» (px). */
const PULL_VISUAL_MAX_PX = 96

function hasEditableFocus(): boolean {
  const active = document.activeElement
  if (!(active instanceof HTMLElement)) {
    return false
  }
  if (active.isContentEditable) {
    return true
  }
  const tag = active.tagName.toUpperCase()
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

function pageScrollTop(): number {
  return window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0
}

function isVerticalScrollContainer(el: HTMLElement): boolean {
  if (el.scrollHeight <= el.clientHeight + 1) {
    return false
  }
  const oy = window.getComputedStyle(el).overflowY
  return oy === 'auto' || oy === 'scroll' || oy === 'overlay'
}

/**
 * Пока внутренний список не у верха — не перехватываем жест
 * (иначе вместо прокрутки списка сработает reload страницы).
 */
function nestedScrollBlocksPull(target: EventTarget | null): boolean {
  let n: Element | null = target instanceof Element ? target : null
  const root = document.body
  while (n != null && n !== root && n !== document.documentElement) {
    if (n instanceof HTMLElement && isVerticalScrollContainer(n) && n.scrollTop > 2) {
      return true
    }
    n = n.parentElement
  }
  return false
}

/**
 * Pull-to-refresh для мобильного CRM: жест вниз от верха страницы (window scroll) → reload.
 */
export function useCrmMobilePullToRefresh(enabled: Ref<boolean>): {
  pullVisualPx: Ref<number>
  pullArmed: Ref<boolean>
} {
  const pullVisualPx = ref(0)
  const pullArmed = ref(false)

  let startY = 0
  let tracking = false
  let attached = false

  function resetPull() {
    tracking = false
    pullVisualPx.value = 0
    pullArmed.value = false
  }

  function onTouchStart(ev: TouchEvent) {
    if (!enabled.value) {
      return
    }
    // A small finger movement on a control must not cancel its native click.
    // Vuetify icons/badges are nested inside the actual button or router link.
    if (
      ev.target instanceof Element &&
      ev.target.closest(
        'a, button, input, textarea, select, label, [role="button"], [role="link"], [contenteditable]:not([contenteditable="false"])',
      )
    ) {
      resetPull()
      return
    }
    if (hasEditableFocus()) {
      resetPull()
      return
    }
    if (pageScrollTop() > 2) {
      resetPull()
      return
    }
    startY = ev.touches[0]?.clientY ?? 0
    tracking = true
    pullArmed.value = false
    pullVisualPx.value = 0
  }

  function onTouchMove(ev: TouchEvent) {
    if (!tracking || !enabled.value) {
      return
    }
    if (pageScrollTop() > 2) {
      resetPull()
      return
    }
    if (nestedScrollBlocksPull(ev.target)) {
      pullVisualPx.value = 0
      pullArmed.value = false
      return
    }
    const y = ev.touches[0]?.clientY ?? 0
    const raw = y - startY
    if (raw <= 0) {
      pullVisualPx.value = 0
      pullArmed.value = false
      return
    }
    try {
      ev.preventDefault()
    } catch {
      /* ignore */
    }
    pullVisualPx.value = Math.min(PULL_VISUAL_MAX_PX, raw * 0.42)
    pullArmed.value = raw >= PULL_RELOAD_RAW_THRESHOLD_PX
  }

  function onTouchEnd(ev: TouchEvent) {
    if (!tracking) {
      resetPull()
      return
    }
    tracking = false
    const t = ev.changedTouches[0]
    const endTarget =
      t != null
        ? document.elementFromPoint(t.clientX, t.clientY)
        : ev.target instanceof Element
          ? ev.target
          : null
    const go =
      pullArmed.value &&
      enabled.value &&
      pageScrollTop() <= 2 &&
      !nestedScrollBlocksPull(endTarget)
    pullVisualPx.value = 0
    pullArmed.value = false
    if (go) {
      // Cache-bust: как у кнопки «Обновить» SPA — не мягкий reload только из bfcache.
      const url = new URL(window.location.href)
      url.searchParams.set('crm_reload_ts', String(Date.now()))
      window.location.replace(url.toString())
    }
  }

  function attach() {
    if (attached || typeof window === 'undefined') {
      return
    }
    document.addEventListener('touchstart', onTouchStart, { passive: true, capture: true })
    document.addEventListener('touchmove', onTouchMove, { passive: false, capture: true })
    document.addEventListener('touchend', onTouchEnd, { passive: true, capture: true })
    document.addEventListener('touchcancel', onTouchEnd, { passive: true, capture: true })
    attached = true
  }

  function detach() {
    if (!attached) {
      return
    }
    document.removeEventListener('touchstart', onTouchStart, true)
    document.removeEventListener('touchmove', onTouchMove, true)
    document.removeEventListener('touchend', onTouchEnd, true)
    document.removeEventListener('touchcancel', onTouchEnd, true)
    attached = false
    resetPull()
  }

  watch(
    enabled,
    (on) => {
      if (on) {
        attach()
      } else {
        detach()
      }
    },
    { immediate: true },
  )

  onMounted(() => {
    if (enabled.value) {
      attach()
    }
  })

  onBeforeUnmount(() => {
    detach()
  })

  return { pullVisualPx, pullArmed }
}
